import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue'

  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import UserPreviewDetails from '@/components/user-management/UserPreviewDetails.vue'

  import useIcons from '@/composables/useIcons'
  import useCommonMixin from '@/composables/useCommonMixin'
  import useI18n from '@/composables/useI18n'
  import useDuration from '@/composables/useDuration'

  import type { PropType } from 'vue'
  import type { MemberGroup, UserMember, UserSessions } from '@/services/interfaces/Auth0'

  import { DateTime } from 'luxon'

  
export default /*#__PURE__*/_defineComponent({
  __name: 'UserTable',
  props: {
    users: {
      required: true,
      type: Array as PropType<UserMember[]>,
    },
    selectedUser: {
      default: null,
      type: Object as PropType<UserMember>,
    },
    inUserManagementTab: {
      type: Boolean,
      default: false,
    },
    userRoleCategory: {
      type: String,
      default: '',
    },
    noUsersText: {
      type: String,
      required: true,
    },
    showTimeSpent: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['set-selected-user', 'delete-user', 'create-new-group', 'reload-user-members'],
  setup(__props, { emit }) {

const props = __props;

  
  
  const { vectorArrow } = useIcons()
  const { formatDate, daysDifferenceFromNow, getInitials, groupBackgroundColor, groupForegroundColor } =
    useCommonMixin()
  const { translateString } = useI18n()
  const { formatDurationToHHMM } = useDuration()

  const sortByName = ref(false)
  const sortByTimeSpent = ref(false)
  const sortByLastActivity = ref(false)

  const mutableUsers = computed((): UserMember[] => {
    return props.users.slice().map((user) => {
      const totalTimeInMinutes = calculateUserTotalSessionTime.value(user.sessions)

      return { ...user, totalSessionTime: totalTimeInMinutes }
    })
  })

  const lastActivityDate = computed(() => {
    return (date: string) => {
      return formatDate(date, 'dd.MM.yyyy')
    }
  })

  const daysDifference = computed(() => {
    return (date: string) => {
      if (!daysDifferenceFromNow(date)) return ''
      const difference = translateString('homepage.daysAgo', { days: daysDifferenceFromNow(date) })
      return `(${difference})`
    }
  })

  const showUserPreviewDetails = computed(() => {
    return (user: UserMember) => {
      if (!props.selectedUser) return false
      return props.inUserManagementTab && props.selectedUser.id === user.id
    }
  })

  const filteredUserGroups = computed(
    () => (userGroups: MemberGroup[]) => userGroups.filter((g) => g.name !== 'All Users'),
  )

  const calculateUserTotalSessionTime = computed(() => (sessions: UserSessions[]) => {
    return sessions.reduce((acc, session) => {
      const sessionStart = DateTime.fromISO(session.startTime)
      const sessionEnd = DateTime.fromISO(session.endTime)
      const sessionDuration = sessionEnd.diff(sessionStart, 'minutes').minutes
      acc += sessionDuration
      return acc
    }, 0)
  })

  const totalUserSessionTime = computed(() => (sessions: UserSessions[]) => {
    if (sessions.length === 0) return '-'
    const totalTime = calculateUserTotalSessionTime.value(sessions)
    return totalTime < 1 ? translateString('homepage.underOneMinute') : `${formatDurationToHHMM(totalTime)}h`
  })

  watch(sortByName, () => sortMembersByName())
  watch(sortByTimeSpent, () => sortMembersByTimeSpent())
  watch(sortByLastActivity, () => sortMembersByLastActivity())

  const sortMembersByName = () => {
    mutableUsers.value.sort((a, b) => {
      if (!a.name || !b.name) return 0
      if (a.name < b.name) return -1
      if (a.name > b.name) return 1
      return 0
    })
    if (sortByName.value) mutableUsers.value.reverse()
  }

  const sortMembersByTimeSpent = () => {
    mutableUsers.value.sort((a, b) => {
      if (a.totalSessionTime < b.totalSessionTime) return -1
      if (b.totalSessionTime > a.totalSessionTime) return 1
      return 0
    })
    if (sortByTimeSpent.value) mutableUsers.value.reverse()
  }

  const sortMembersByLastActivity = () => {
    mutableUsers.value.sort((a, b) => {
      if (!a.lastActivityDate || !b.lastActivityDate) return 0
      const aTime = DateTime.fromISO(a.lastActivityDate)
      const bTime = DateTime.fromISO(b.lastActivityDate)
      if (aTime < bTime) return -1
      if (bTime > aTime) return 1
      return 0
    })
    if (sortByLastActivity.value) mutableUsers.value.reverse()
  }

  const selectUser = (user: UserMember) => {
    // only allow selecting in user management view for now
    if (props.inUserManagementTab) {
      if (props.selectedUser === user) {
        // deselect user
        emit('set-selected-user', null)
        return
      }
      emit('set-selected-user', user)
    }
  }

return { __sfc: true,props, emit, vectorArrow, formatDate, daysDifferenceFromNow, getInitials, groupBackgroundColor, groupForegroundColor, translateString, formatDurationToHHMM, sortByName, sortByTimeSpent, sortByLastActivity, mutableUsers, lastActivityDate, daysDifference, showUserPreviewDetails, filteredUserGroups, calculateUserTotalSessionTime, totalUserSessionTime, sortMembersByName, sortMembersByTimeSpent, sortMembersByLastActivity, selectUser, computed, ref, watch, SVGRenderer, UserPreviewDetails, useIcons, useCommonMixin, useI18n, useDuration, DateTime }
}

})